import type { SelectedDays } from '@/types/SearchTypes';
import { useLazyQuery } from '@apollo/client';
import {
	dataDate,
	getLongDayOfWeek,
	seasonDayRange,
	todaySubtract,
} from 'features/dates/date-helpers';
import { useGetRecReviewDataQuery } from 'features/my-view/gql/_gen_/rec-review.gql';
import type { PropSeason, SearchInfo } from 'graphql/gql-types';
import { GET_REC_REVIEW_DATA } from 'queries/gqlRecReview';
import type { SeasonType } from 'types/RecReviewTypes';
import { SeasonPriority } from 'types/SeasonTypes';

const EMPTY_SEASON = {
	date: '',
	property_id: '',
	season_id: '',
	season_name: '',
	season_start: null,
	season_end: null,
	selected_days: [],
	highlight_myview: true,
	max_rate: null,
	min_rate: null,
	season_priority: SeasonPriority.Highest,
};
export const useRecReview = (input?: SearchInfo, propertyId?: string) => {
	return useGetRecReviewDataQuery({
		skip: !propertyId || !input?.userId || !input,
		variables: {
			propertyId,
			input: {
				...input,
				startDate: todaySubtract(1, 'week'),
			},
		},
	});
};

export const useLazyGetAskRevData = () => {
	return useLazyQuery(GET_REC_REVIEW_DATA);
};

export const useSelloutRecReview = (
	input?: SearchInfo,
	propertyId?: string,
) => {
	return useLazyQuery(GET_REC_REVIEW_DATA, {
		variables: {
			propertyId,
			input,
			userId: input?.userId,
		},
	});
};

export const getSeasonFormatData = (seasonData?: PropSeason[]) => {
	const seasonListByDateRange: SeasonType[] = [];
	const results: { [date: string]: SeasonType } = {};

	if (seasonData) {
		for (const season of seasonData) {
			if (season.highlight_myview) {
				// Utilize seasonDayRange to get the list of dates for the season. This function
				// limits the number of dates to 400. If the season is longer than 400 days, we
				// only want to display the first 400 days starting with 1 week prior to today.
				const dates =
					season.season_start && season.season_end
						? seasonDayRange(season.season_start, season.season_end)
						: [];
				for (const stay_date of dates) {
					// Only include the season on the stay date if it applies to the day of week.
					if (
						season.selected_days.length > 0 &&
						season.selected_days.includes(getLongDayOfWeek(stay_date))
					) {
						const seasonObj: SeasonType = EMPTY_SEASON;
						seasonObj.date = stay_date;
						seasonListByDateRange.push({
							...seasonObj,
							...season,
							selected_days: season.selected_days.map(
								(day) => day as SelectedDays,
							),
						});
					}
				}
			}
		}
	}

	for (const season of seasonListByDateRange) {
		if (season.date in results) {
			if (season.season_priority > results[season.date].season_priority) {
				results[season.date] = {
					...season,
					season_start: dataDate(season.season_start),
					season_end: dataDate(season.season_end),
				};
			}
		} else {
			results[season.date] = {
				...season,
				season_start: dataDate(season.season_start),
				season_end: dataDate(season.season_end),
			};
		}
	}
	return results;
};
